import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import type { S132Period, S132Record } from "@thalamos/aspire-common";
import { MhaStatus, s132StatusIsOutstanding } from "@thalamos/aspire-common";

import {
  RecordS132AttemptDialog,
  type S132AttemptSubmission,
} from "./RecordS132AttemptDialog.js";
import { saveS132Attempt } from "./S132HistoryDialogContainer.js";
import type { S132PromptFor } from "./s132PromptQueue.js";
import { s132PeriodChoice, s132SectionChoices } from "./s132Sections.js";
import { s132DutyProvision } from "./S132StatusPill.js";
import { apiHooks } from "~/api.js";
import { renderSuccessToast } from "~/components/design-system/index.js";

export type S132PostSignPromptViewProps = {
  patientName: string;
  recorderName?: string;
  record: S132Record;
  promptFor: S132PromptFor;
  onLater: () => void;
  onSave: (attempt: S132AttemptSubmission) => Promise<S132Record>;
  onSaved: (record: S132Record) => void;
};

const outstandingPeriod = (
  record: S132Record,
  promptFor: S132PromptFor,
): S132Period | undefined =>
  record.periods.find(
    (p) =>
      p.current &&
      s132StatusIsOutstanding(p.status) &&
      (p.section === MhaStatus.Section136) === (promptFor === "section136"),
  );

/** Opens straight onto the Record form; "Not now" leaves the duty showing on the patient banner. */
export function S132PostSignPromptView(props: S132PostSignPromptViewProps) {
  const current = outstandingPeriod(props.record, props.promptFor);

  return (
    <RecordS132AttemptDialog
      patientName={props.patientName}
      recorderName={props.recorderName}
      // The queued prompt names the section owed, so it is the only choice.
      sections={
        current ? [s132PeriodChoice(current)] : s132SectionChoices(props.record)
      }
      defaultSection={current?.section}
      intro={`Record if the patient has been given their ${s132DutyProvision(
        current?.duty ?? "s132",
      )} information, or if this needs to be reattempted later.`}
      cancelLabel="Not now"
      onClose={props.onLater}
      onSave={props.onSave}
      onSaved={props.onSaved}
    />
  );
}

export type S132PostSignPromptProps = {
  formContextId: string;
  patientId: string;
  patientName: string;
  recorderName?: string;
  /** The form-context page's own path; the prompt waits while any sub-route (the page's route-driven dialogs) is showing. */
  formContextPath: string;
  queuedFor: S132PromptFor | undefined;
  onDone: () => void;
};

export function S132PostSignPrompt(props: S132PostSignPromptProps) {
  const location = useLocation();
  const idle = location.pathname === props.formContextPath;
  const { queuedFor, onDone } = props;
  const [{ data: record }] = apiHooks.s132.record(
    props.patientId,
    !queuedFor || !idle,
  );

  const owed =
    record && queuedFor ? outstandingPeriod(record, queuedFor) : undefined;
  // Nothing owed for the section the prompt is for means it is spent; clear it
  // so it cannot surface later.
  useEffect(() => {
    if (queuedFor && record && !owed) onDone();
  }, [queuedFor, record, owed, onDone]);

  if (!queuedFor || !idle || !record || !owed) return null;
  const provision = s132DutyProvision(owed.duty);

  return (
    <S132PostSignPromptView
      patientName={props.patientName}
      recorderName={props.recorderName}
      record={record}
      promptFor={queuedFor}
      onLater={props.onDone}
      onSave={(submission) => saveS132Attempt(props.patientId, submission)}
      onSaved={() => {
        renderSuccessToast({ message: `${provision} attempt recorded` });
        props.onDone();
      }}
    />
  );
}
